import React, { createContext, FC, useState, useEffect, ReactNode, useContext } from 'react';
import { IWebsite, fetchWebsites } from '../api';

interface WebsiteContextValue {
  websiteId: string;
}

const WebsiteContext = createContext<WebsiteContextValue | undefined>(undefined);

interface Props {
  websiteId?: string;
  children: ReactNode;
}

export const WebsiteProvider: FC<Props> = ({ children, websiteId: initialWebsiteId }) => {
  const [websiteId, setWebsiteId] = useState<string | undefined>(initialWebsiteId);
  const [website, setWebsite] = useState<IWebsite | null>(null); // ✅ Uniquement pour la toolbar
  const [websites, setWebsites] = useState<IWebsite[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    if (initialWebsiteId) {
      return;
    }

    try {
      setLoading(true);
      const data = await fetchWebsites();
      setWebsites(data);
    } catch (error) {
      console.error('Could not load websites:', error);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchData() }, []);

  if (initialWebsiteId) {
    return (
      <WebsiteContext.Provider value={{ websiteId: initialWebsiteId }}>
        {children}
      </WebsiteContext.Provider>
    );
  }

  const handleWebsiteChange = (selectedId: string) => {
    const selected = websites.find(w => w.id === selectedId);
    if (selected) {
      setWebsiteId(selectedId);
      setWebsite(selected);
    }
  };

  return (
    <WebsiteContext.Provider value={{ websiteId: websiteId! }}>
      <div className="row align-items-center">
        <div className="col-auto">
          {loading ? (
            <div className="spinner-border spinner-border-sm" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          ) : (
            <select
              id="website-select"
              className="form-select form-select-sm"
              onChange={(e) => handleWebsiteChange(e.target.value)}
              value={websiteId || ""}
            >
              <option value="" disabled>
                -- Select Website --
              </option>
              {websites.map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
            </select>
          )}
        </div>
        {website && (
          <div className="col-auto">
            <span className="text-muted">- {website.domain}</span>
          </div>
        )}
      </div>
      <div className='separator separator-dashed my-5'></div>
      
      {websiteId ? (
        children
      ) : (
        <div className="container">
          <div className="alert alert-info text-center" role="alert">
            You must select a website
          </div>
        </div>
      )}
    </WebsiteContext.Provider>
  );
};

export const useWebsite = () => {
  const context = useContext(WebsiteContext);
  if (context === undefined) {
    throw new Error('useWebsite expects to be in WebsiteProvider');
  }
  return context;
};
